import { FC, useEffect, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import { VStack, Input, useToast } from "@chakra-ui/react";
import {
    Typology as Entity,
    TypologyApi as EntityApi,
    Training,
    TrainingApi,
} from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { AppFormContainer } from "../../../containers";
import {
    AppFormControl,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import { APPC } from "../../../config";

export const TypologyForm: FC<{
    formContainerState: FormContainerStateReturnType;
}> = ({ formContainerState }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();

    // state & const
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Entity>(new Entity());

    const [loadingTrainings, setLoadingTrainings] = useState(true);
    const [trainingOptions, setTrainingOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTraining, setSelectedTraining] =
        useState<AppSelectOption | null>(null);
    const [totalTrainings, setTotalTrainings] = useState(0);
    const [trainingPage, setTrainingPage] = useState(1);
    const [trainingSearch, setTrainingSearch] = useState("");

    const {
        handleSubmit,
        register,
        reset,
        control,
        formState: { errors },
    } = useForm<Entity>({
        resolver: yupResolver(schema(t)),
        mode: "onSubmit",
    });

    // handler
    const onSubmitHandler = (formData: Entity) => {
        setLoading(true);
        EntityApi.createOrUpdate<Entity, Entity>(
            formContainerState.entityId,
            formData
        )
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    const loadTrainingOptions = () => {
        setLoadingTrainings(true);
        const params = {
            [`order[name]`]: "asc",
        };

        if (trainingSearch) {
            params["name"] = trainingSearch;
        }

        TrainingApi.getCollection<Training>(trainingPage, params)
            .then(({ response }) => {
                if (response !== null) {
                    setTotalTrainings(response.totalItems);
                    const newOptions =
                        response.items?.map((e) => ({
                            value: TrainingApi.toResourceIRI(e.id),
                            label: e.name,
                        })) || [];
                    trainingPage === 1
                        ? setTrainingOptions(newOptions)
                        : setTrainingOptions((prevOptions) => [
                              ...prevOptions,
                              ...newOptions,
                          ]);
                }
            })
            .finally(() => {
                setLoadingTrainings(false);
            });
    };

    // useEffect
    useEffect(() => {
        loadTrainingOptions();
    }, [trainingPage, trainingSearch]);

    useEffect(() => {
        if (!formContainerState.entityId) {
            reset();
            setData(new Entity());
            return;
        }
        setLoading(true);
        EntityApi.getItem<Entity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    if (
                        response.training &&
                        typeof response.training !== "string"
                    ) {
                        setSelectedTraining({
                            value: TrainingApi.toResourceIRI(
                                response.training.id
                            ),
                            label: response.training.name,
                        });
                    }
                    setData(response);
                }
            })
            .finally(() => setLoading(false));
    }, [formContainerState.entityId]);

    return (
        <AppFormContainer
            title={t("padm.TypologyForm:text.pageTitle")}
            state={formContainerState}
            type={APPC.FORM_CONTAINER}
        >
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"}>
                    <AppFormControl
                        label={t("ent.Typology:training.label")}
                        isLoading={loading}
                        isInvalid={!!errors.training}
                        message={errors.training?.message}
                    >
                        <Controller
                            control={control}
                            name="training"
                            defaultValue={TrainingApi.toResourceIRI(
                                (data?.training as Training)?.id
                            )}
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loadingTrainings}
                                    options={trainingOptions}
                                    value={selectedTraining}
                                    placeholder={t(
                                        "ent.Typology:training.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedTraining(
                                            e as AppSelectOption
                                        );
                                    }}
                                    onMenuScrollToBottom={() => {
                                        if (
                                            trainingOptions.length >=
                                            totalTrainings
                                        ) {
                                            return;
                                        }
                                        setTrainingPage(
                                            (prevPage) => prevPage + 1
                                        );
                                    }}
                                    onInputChange={(inputValue: string) => {
                                        if (inputValue !== trainingSearch) {
                                            setTrainingSearch(inputValue);
                                            if (trainingPage !== 1) {
                                                setTrainingPage(1);
                                            }
                                            setTrainingOptions([]);
                                        }
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl
                        label={t("ent.Typology:name.label")}
                        isInvalid={!!errors.name}
                        message={errors.name?.message}
                        isLoading={loading}
                    >
                        <Input
                            type="text"
                            placeholder={t("ent.Typology:name.placeholder")}
                            {...register("name")}
                            defaultValue={data.name}
                        />
                    </AppFormControl>
                </VStack>
            </form>
        </AppFormContainer>
    );
};
